import { Injectable } from '@angular/core';
import {
  DataService as CoreDataService,
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  ReadListDataSourceModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { ResourceManager } from '@barthauer/abc/core/resources';
import { UserService } from '@barthauer/basys/core/authentication';
import { Address } from '@barthauer/custom/arena';
import { Department, DocumentKind, GeoDsDocument, Measure, Person } from '@barthauer/custom/xmt-models';
import config from 'devextreme/core/config';
import { exportDataGrid as exportExcelDataGrid } from 'devextreme/excel_exporter';
import { exportDataGrid as exportPdfDataGrid } from 'devextreme/pdf_exporter';
import repaintFloatingActionButton from 'devextreme/ui/speed_dial_action/repaint_floating_action_button';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';
import {
  NotificationMessage,
  PermessionType,
  PickListLanguage,
  ROLES,
  ToastType,
  UserRoles,
} from '../models/enumeration-list.model';
import { Permession } from '../user-right/user-settings/user-settings.component';
import { AppService } from './app.service';
import { NotificationService } from './notification.service';
import { ISkipProfileSelectionParams } from '../models/interfaces.model';
export enum ObjectKeys {
  ADDRESS = 'Address',
  MAILADDRESS = 'MailAddress',
  PHONENUMBER = 'PhoneNumber',
  COLLABORATOR = 'xmtMeasureTaskCollaborator',
  DOCUMENT = 'Document',
  EVENT = 'xmtSubProjectEvent',
  MEASURE = 'xmtMeasure',
  MEASUREPROGRESS = 'xmtMeasureProgress',
  PERSON = 'Person',
  PICKLISTITEM = 'PicklistItem',
  RESOURCE = 'xmtMeasureResource',
  RESPONSIBLEPLAN = 'xmtResponsiblePlan',
  SUBPROJECT = 'xmtSubProject',
  PROJECT = 'xmtProject',
  STAFFMEMBER = 'xmtStaffMember',
  TAG = 'xmtMeasureTag',
  TASK = 'xmtMeasureTask',
  SUPPORTTASK = 'Task',
  DEPARTMENT = 'Department',
  ADDRESSPERSON = 'AddressPerson',
  COMMENT = 'Comment',
  PROPERTY = 'Property',
  PICKLIST = 'Picklist',
}
@Injectable()
export class DataService {
  private measureDetailSubject = new BehaviorSubject<Measure>(null);
  private readonly objectSpaceName = 'ABC.Applications.KAOS.Data';
  private gridDataSubject = new Subject<any>();
  private uploadData: Subject<GeoDsDocument> = new Subject();
  public updateGridData$ = this.gridDataSubject.asObservable();
  public uploadData$: Observable<GeoDsDocument> = this.uploadData.asObservable();
  public cachedResponsiblesResponse: Person[] = [];
  public cachedResponsiblesResponseWithImages: Person[] = [];
  public cachedProfileAvatar: string[] = [];
  public cachedCompany: Address[] = [];
  public cachedDepartement: Department[] = [];
  public favoriteMeasureId = new Subject<string>();
  public addressPersonId: string;
  public favoriteMeasureId$ = this.favoriteMeasureId.asObservable();
  public res = ResourceManager.getResources('ubt-geods');
  public returnPath: string;
  public profilId: string;
  public ROLES = ROLES;
  public unothorized = false;

  private redirectParameters: ISkipProfileSelectionParams = {};
  public readonly avatarColumns = [
    this.coreDataService.createQueryColumn('Id', 'Id'),
    {
      Name: 'Object',
      OPath: 'Object',
      Format: 'Base64Image[300x300]',
    },
  ];

  public PermessionType = PermessionType;
  public userRoles: string[] = [];
  private rolesLoaded = false;
  private permissionsLoaded = false;
  public userAvatar: string;
  public currentUserName: string;
  public userRole = UserRoles.BROKER;
  public readonly profileColumns = [
    this.coreDataService.createQueryColumn('Id', 'Id'),
    this.coreDataService.createQueryColumn('ParentId', 'ParentId'),
    this.coreDataService.createQueryColumn('FirstName', 'FirstName'),
    this.coreDataService.createQueryColumn('LastName', 'LastName'),
    this.coreDataService.createQueryColumn('Function', 'Function'),
  ];
  public readonly adddressPersonQueryColumns: QueryColumn[] = [
    this.coreDataService.createQueryColumn('Position', 'Position'),
    this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
  ];
  public permessions: Map<string, boolean> = new Map<string, boolean>();

  public readonly permessionColumuns: QueryColumn[] = [
    this.coreDataService.createQueryColumn('ParentId', 'ParentId', QueryColumnSortOrder.None),
    this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
    this.coreDataService.createQueryColumn('Designation', 'permessionName', QueryColumnSortOrder.None),
    this.coreDataService.createQueryColumn('TextValue', 'module', QueryColumnSortOrder.None),
    this.coreDataService.createQueryColumn('BoolValue', 'permession', QueryColumnSortOrder.None),
  ];
  constructor(
    public appService: AppService,
    public coreDataService: CoreDataService,
    private persistenceService: PersistenceService,
    private notificationService: NotificationService,
    private userSevice: UserService
  ) { }

  prepareUserData = (): Observable<any> => {
    if (!sessionStorage.getItem('profileId')) {
      return this.userSevice.getCurrentLoginInfo().pipe(
        switchMap((_loginInfo) => {
          sessionStorage.setItem('currentProfile', _loginInfo?.displayName);
          return this.prepareProfile(_loginInfo).pipe(
            switchMap((_profileInfo) => {
              if (Object.keys(_profileInfo).length > 0) {
                sessionStorage.setItem('profileAddressId', _profileInfo[0].ParentId);
                sessionStorage.setItem('profileId', _profileInfo[0].Id);
                this.profilId = _profileInfo[0].Id;

                return this.prepareProfileAvatar(_profileInfo[0].Id).pipe(
                  map((_profileAvatar) => ({
                    _loginInfo,
                    _profileInfo,
                    _profileAvatar,
                  }))
                );
              } else {
                return of([]).pipe(
                  map((_profileAvatar) => ({
                    _loginInfo,
                    _profileInfo,
                    _profileAvatar,
                  }))
                );
              }
            })
          );
        })
      );
    } else {
      return of(undefined);
    }
  };

  public loadUserRole(addressId: string, personId: string): Observable<any> {
    if (this.rolesLoaded && this.permissionsLoaded) {
      return of({
        roles: this.userRoles,
        permissions: this.permessions,
      });
    }

    if (this.rolesLoaded) {
      return this.getPermissionsForRole(this.userRoles[0]).pipe(
        map((permissions) => {
          this.storePermissionsByModule(permissions);
          return {
            roles: this.userRoles,
            permissions: this.permessions,
          };
        })
      );
    }

    const addressPersonOPath = `ParentId='${addressId}' And And IdRefPerson='${personId}'`;

    return this.readObjects(ObjectKeys.ADDRESSPERSON, this.adddressPersonQueryColumns, addressPersonOPath).pipe(
      map((roles) => {
        if (roles?.length) {
          this.userRoles = roles.map((roleObj) => roleObj['Position']);
          this.rolesLoaded = true;
          return roles;
        }
        return null;
      }),
      switchMap((roles) => {
        if (roles && roles.length > 0) {
          this.addressPersonId = roles[0]['Id'];
          return this.LoadPermission(roles[0]['Id']).pipe(
            tap((permissions) => {
              this.permissionsLoaded = true;
              this.storePermissionsByModule(permissions);
            }),
            map((permissions) => ({
              roles: this.userRoles,
              permissions: this.permessions,
            }))
          );
        }
        return of({ roles: this.userRoles, permissions: new Map() });
      }),
      catchError((error) => {
        console.error('Error loading user roles or fetching permissions:', error);
        return of({ roles: [], permissions: [] });
      })
    );
  }

  public getPermissionsForRole(roleId: string): Observable<any> {
    if (this.permessions.size > 0) {
      return of(this.permessions);
    }

    return this.LoadPermission(roleId).pipe(
      map((permissionsByModule) => {
        this.storePermissionsByModule(permissionsByModule);
        return permissionsByModule;
      }),
      catchError((error) => {
        console.error('Error fetching permissions:', error);
        return of({});
      })
    );
  }

  public storePermissionsByModule(permession: Permession[]) {
    if (!this.permessions) {
      this.permessions = new Map();
    }

    const permissionsMap = permession.reduce((map, permission) => {
      map.set(permission.permessionName, permission.permession);
      return map;
    }, new Map<string, boolean>());
    this.permessions = permissionsMap;
  }
  public LoadPermission = (addressPersonId: string): Observable<any> => {
    const pickListLanguage = sessionStorage.getItem('pickListLanguage')
      ? sessionStorage.getItem('pickListLanguage')
      : PickListLanguage.German;
    const permissionOPath = `ParentId = Type(PicklistItem)[Picklist.Name = 'Personenposition' And Picklist.Language = '${pickListLanguage}' And  Value = Type(AddressPerson)[Id ='${addressPersonId}'].Position].Id`;

    return this.readObjects(ObjectKeys.PROPERTY, this.permessionColumuns, permissionOPath).pipe(
      first(),
      map((permissions) => {
        if (Array.isArray(permissions) && permissions.length > 0) {
          return permissions;
        }
        return [];
      }),
      catchError((error) => {
        return of([]);
      })
    );
  };
  public getBasePath(path: string): string {
    const cleanedPath = path.replace(/^\//, '');

    const basePath = cleanedPath.split('/:')[0].split('/')[0];

    return basePath;
  }

  public hasNonAuthrisedRole(roles: string[]): boolean {
    return !roles.some((role) => this.userRoles.includes(role));
  }
  public getRole(): string[] {
    return this.userRoles;
  }
  public setRole(roles: string[]) {
    this.userRoles = roles;
  }

  public hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.userRoles.includes(role));
  }
  public clearRoles(): void {
    this.userRoles = [];
    this.rolesLoaded = false;
    this.permissionsLoaded = false;
    this.permessions = new Map<null, null>();
  }
  public getAllPermessions(): Map<string, boolean> {
    return this.permessions;
  }

  public checkPermissionForAdministrationMenu(modules: string[]): boolean {
    if (modules && modules.length > 0) {
      for (const module of modules) {
        const key = `${module}-${PermessionType.SEE}`;
        const permission = this.getAllPermessions().get(key);
        if (permission) {
          return true;
        }
      }

      return false;
    }

    return true;
  }

  public hasAccessPermesssion(module: string, object: Map<string, Permession>): string {
    const accesPermession = object.get(module);

    if (accesPermession) {
      return accesPermession.permession.toString()[0];
    }
    return '';
  }
  public hasPermessionForSpecificModule(module: string, PermType: PermessionType): boolean {
    const key = `${module}-${PermType}`;
    const hasPermission = this.permessions.get(key);

    return hasPermission ?? false;
  }

  private prepareProfile(userName) {
    const profileOPath = 'Login=' + "'" + userName.userName + "'";
    return this.readObjects(ObjectKeys.PERSON, this.profileColumns, profileOPath);
  }

  private prepareProfileAvatar(profileId: string) {
    const profileAvatarOPath = 'ParentId=' + "'" + profileId + "'";
    return this.readObjects(ObjectKeys.DOCUMENT, this.avatarColumns, profileAvatarOPath);
  }
  public setProfileInformations = (profileInfo): void => {
    this.userAvatar = profileInfo._profileAvatar[0]?.Object;
    if (profileInfo._loginInfo) {
      this.currentUserName = profileInfo._loginInfo?.displayName || profileInfo._loginInfo?.userName;
    } else {
      this.currentUserName = sessionStorage.getItem('currentProfile');
      this.userAvatar = sessionStorage.getItem('userAvatar');
    }

    if (profileInfo._loginInfo?.isAdmin === true) {
      this.userRole = UserRoles.MANAGINGDIRECTOR;
    }
  };
  public readObjects<T>(objectType: string, columns: QueryColumn[], OPath?: string): Observable<any> {
    const readObjectQuery: QueryObjectsModel = new QueryObjectsModel();
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = objectType;
    mainQuery.Columns = columns;
    mainQuery.OPath = OPath;
    if (OPath) {
      mainQuery.OPath = OPath;
    }
    readObjectQuery.ObjectQueries = [mainQuery];
    return this.coreDataService.executeReadObjectsQuery(readObjectQuery);
  }

  public deleteObject(objectKey: ObjectKeys, id: string | number): Observable<any> {
    const persistQuery: PersistObjectModel = new PersistObjectModel();
    persistQuery.ObjectSpaceName = this.objectSpaceName;
    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = PersistMode.Delete;
    targetObject.ObjectKey = new ObjectKey();
    targetObject.ObjectKey.Id = id.toString();
    targetObject.ObjectKey.ObjectType = objectKey;
    persistQuery.Object = targetObject;
    return this.persistenceService.executePersistObjectQuery(persistQuery);
  }

  public updateGridData(data: any) {
    this.gridDataSubject.next(data);
  }

  public getMeasureDetail(): Observable<Measure> {
    return this.measureDetailSubject.asObservable();
  }

  public sendMeasureDetail(measureAssessment: Measure) {
    this.measureDetailSubject.next(measureAssessment);
  }

  public checkGridGroupByColumns(key: string, groupByColumnIndex) {
    if (localStorage.getItem(key)) {
      const gridState = JSON.parse(localStorage.getItem(key));
      gridState.columns.map((item) => {
        if (item.groupIndex) {
          groupByColumnIndex[item.dataField] = item.groupIndex;
          return groupByColumnIndex;
        }
      });
    }
  }
  public prepareStaffMembersWithDocuments(): QueryObjectsModel {
    const personColumns = [
      this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('FirstName', 'FirstName', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('LastName', 'LastName', QueryColumnSortOrder.None),
    ];

    const staffMemberOpath =
      'ParentId=' + "'" + localStorage.getItem('currentCompany') + "'" + " && Not(LastName = 'Admin')";
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = ObjectKeys.PERSON;
    mainQuery.OPath = staffMemberOpath;
    mainQuery.Columns = personColumns;
    
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [mainQuery];
    return queryDocument;
  }

  public prepareStaffMemberList(): QueryObjectsModel {
    const person: Query = new Query();
    person.ObjectType = ObjectKeys.PERSON;

    const columns = [
      this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('FirstName', 'FirstName', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('LastName', 'LastName', QueryColumnSortOrder.None),
    ];

    person.Columns = columns;

    person.OPath = `ParentId='${localStorage.getItem('currentCompany')}' && Not(LastName = 'Admin')`;

    const addressPerson: Query = new Query();
    addressPerson.ObjectType = ObjectKeys.ADDRESSPERSON;
    addressPerson.Name = 'AddressPerson';
    addressPerson.OPath = 'AddressPerson_IdRefPerson_Person';
    addressPerson.Columns = this.adddressPersonQueryColumns;

    person.ObjectQueries = [addressPerson];

    const personDetailsColumns = new QueryObjectsModel();
    personDetailsColumns.ObjectQueries = [person];

    return personDetailsColumns;
  }

  public prepareStaffMembers(): Observable<any> {
    return this.coreDataService.executeReadObjectsQuery(this.prepareStaffMemberList());
  }

  public getDataType(dataType) {
    switch (dataType) {
      case 'String':
        return 'string';
      case 'Integer32':
        return 'number';
      case 'Date':
        return 'date';
      default:
        return 'string';
    }
  }
  public readonly documentbyIdColumns: QueryColumn[] = [
    this.coreDataService.createQueryColumn('Id', 'Id'),
    this.coreDataService.createQueryColumn('Object', 'Object'),
  ];

  public previewDocument = (document) => {
    const documentId = document.Id;
    const _documentOpath = `Id = '${documentId}'`;
    const documentDesignation = document.Designation;
    const pdfViewer = window.open('');
    pdfViewer.document.write('<title>' + documentDesignation + '</title>');
    this.readObjects(ObjectKeys.DOCUMENT, this.documentbyIdColumns, _documentOpath).subscribe(
      (_document) => {
        pdfViewer.document.write(
          `<iframe width='100%' height='100%' src='data:${this.getMimeType(document)};base64,${encodeURI(
            _document[0].Object
          )}'></iframe>`
        );
      },
      (error) => {
        console.error(error);
      }
    );
  };

  public getResponsibleFullName(account: Person): string {
    return account ? account?.LastName + ', ' + account?.FirstName : '';
  }

  public customizePercentageText(event) {
    return event ? event + '%' : 0 + '%';
  }

  public loadListDataSource(objectType: string, columnName: string): Observable<any> {
    const readListDataSourceQuery = new ReadListDataSourceModel();
    const objectKey = new ObjectKey();
    objectKey.ObjectType = objectType;
    readListDataSourceQuery.ObjectKey = objectKey;
    readListDataSourceQuery.OutputMode = 0;
    readListDataSourceQuery.ColumnName = columnName;
    return this.coreDataService.executeReadListDataSource(readListDataSourceQuery);
  }

  public persistUploadDocument(document: GeoDsDocument): void {
    const documentPersistQuery: TargetObjectData = new TargetObjectData();
    documentPersistQuery.ObjectKey = new ObjectKey();
    documentPersistQuery.ObjectKey.ObjectType = ObjectKeys.DOCUMENT;
    const documentColumns: TargetColumnValue[] = [
      { Name: 'Object', Value: this.getBinaryStringFromUrl(document?.Object) },
      { Name: 'Designation', Value: document?.Designation },
      { Name: 'FileName', Value: document?.FileName },
      { Name: 'ParentId', Value: document?.ParentId },
      { Name: 'Kind', Value: document?.Kind },
      { Name: 'ParentObjectType', Value: document?.ParentObjectType },
    ];
    if (document?.Id !== undefined && document?.Id !== null) {
      documentPersistQuery.ObjectKey.Id = document?.Id;
      documentPersistQuery.Mode = PersistMode.Update;
    } else {
      documentPersistQuery.Mode = PersistMode.Insert;
    }
    documentPersistQuery.TargetColumns = documentColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = documentPersistQuery;
    this.persistenceService.executePersistObjectQuery(persistObject).subscribe(
      (data) => {
        this.updateGridData(data);
        this.notificationService.callNotification({
          message: NotificationMessage.ValidUploadMessage,
          type: ToastType.SUCCESS,
        });
      },
      (error) => {
        this.notificationService.callNotification({ message: NotificationMessage.Error, type: ToastType.ERROR });
      }
    );
  }

  private getBinaryStringFromUrl(url: string): string {
    const img = url?.split(',')[1];
    if (img) {
      return img;
    } else {
      return url;
    }
  }

  public onFileChanged(event, document: GeoDsDocument): void {
    let documentSource = '';
    let fileName = '';
    if (event?.value !== null) {
      const file = event?.value[0];
      if (file?.name !== undefined) {
        fileName = file.name;
      }
      const reader = new FileReader();
      reader.readAsDataURL(new Blob([file]));
      reader.onloadend = () => {
        documentSource = reader.result as string;
        document.Object = documentSource;
        document.FileName = fileName;
        this.uploadData.next(document);
      };
    }
  }

  public downloadFile(doc: GeoDsDocument) {
    const a = document.createElement('a');
    a.href = `data:${this.getMimeType(doc)};base64,${doc.Object}`;
    a.download = doc.FileName;
    a.click();
  }

  private getMimeType(doc: GeoDsDocument, ext?: string) {
    const _extension = doc.FileName.split('.').pop();
    const extension = (ext || _extension).toLowerCase();
    switch (extension) {
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'JPG':
        return 'image/jpeg';
      case 'pdf':
      case 'zip':
        return 'application/' + extension;
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'ppt':
        return 'application/vnd.ms-powerpoint';
      case 'pptx':
        return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case 'txt':
        return 'text/plain';
      case 'xls':
        return 'application/vnd.ms-excel';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'csv':
        return 'text/csv';
      default:
        const fileNameExt = doc.FileName?.split('.').pop();
        if (fileNameExt && doc.FileName?.indexOf('.') > -1) {
          return this.getMimeType(doc, fileNameExt);
        } else if (doc.FileName.indexOf('.') === -1) {
          if (doc.Kind) {
            switch (doc.Kind) {
              case DocumentKind.Bild:
                return 'image/jpeg';
              default:
                return 'application/octet-stream';
            }
          }
        } else {
          return 'application/octet-stream';
        }
    }
  }

  public floatingButtonConfig() {
    config({
      floatingActionButtonConfig: {
        icon: 'rowfield',
        shading: true,
        direction: 'down',
        position: {
          of: '#grid',
          my: 'right bottom',
          at: 'right bottom',
          offset: '-16 -16',
        },
      },
    });
    repaintFloatingActionButton();
  }

  public setRedirectParams = (skipProfileParameters: ISkipProfileSelectionParams): void => {
    this.redirectParameters = skipProfileParameters;
  }

  public getRedirectParams = (): ISkipProfileSelectionParams => {
    return this.redirectParameters;
  }

  public clearRedirectParams = (): void => {
    this.redirectParameters = {};
  }

  public onExporting = (e, fileName: string): void => {
    if (e.format === 'xlsx') {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet(fileName);
      exportExcelDataGrid({
        component: e.component,
        worksheet,
        autoFilterEnabled: true,
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName + '.' + e.format);
        });
      });
    } else {
      const doc = new jsPDF();
      exportPdfDataGrid({
        jsPDFDocument: doc,
        component: e.component,
        indent: 5,
      }).then(() => {
        doc.save(fileName + '.' + e.format);
      });
    }
  };
}
